'use client';

import { useIsomorphicEffect } from '@ocodelib/mui/hooks';
import { createContext, useEffect, useMemo, useRef } from 'react';
import { JsBasicAppManager } from './JsBasicAppManager';
import { useJsBasicAppLoader } from './app';
import type { IAppConfig, IJsBasicLayoutConfig, JsBasicAppType } from './types';

export interface IJsBasicAppContext {
  appManager: JsBasicAppManager;
  app: JsBasicAppType;
}

export const JsBasicAppContext = createContext<IJsBasicAppContext | null>(null);

interface Props {
  layoutConfig: IJsBasicLayoutConfig;
  appConfig: IAppConfig;
  children: React.ReactNode;
  onLoadApp?: (app: JsBasicAppType | null) => void;
}

export function JsBasicAppProvider(props: Props) {
  const { layoutConfig, children } = props;
  const appConfigRef = useRef(props.appConfig);
  appConfigRef.current = props.appConfig;
  const onLoadAppRef = useRef(props.onLoadApp);
  onLoadAppRef.current = props.onLoadApp;

  const appManager = useMemo(() => new JsBasicAppManager(appConfigRef.current), []);
  const { app } = useJsBasicAppLoader({
    previewMode: props.appConfig.previewMode ?? false,
    newProjectLoader: props.appConfig.newProjectLoader,
    initialProjectLoader: props.appConfig.initialProjectLoader,
  });

  useIsomorphicEffect(() => {
    appManager.setup(layoutConfig ?? null);
  }, [appManager, layoutConfig]);

  useIsomorphicEffect(() => {
    appManager.setApp(app ?? null);
  }, [appManager, app]);

  const contextData: IJsBasicAppContext | null = useMemo(
    () =>
      app
        ? {
            appManager,
            app,
          }
        : null,
    [appManager, app],
  );

  useEffect(() => {
    const onLoadApp = onLoadAppRef.current;
    if (!onLoadApp) return;
    onLoadApp(app ?? null);
    return () => {
      onLoadApp(null);
    };
  }, [app]);

  if (!contextData) return null;
  return <JsBasicAppContext.Provider value={contextData}>{children}</JsBasicAppContext.Provider>;
}
